import React, { FunctionComponent, useEffect, useState, useMemo } from 'react';
import DashboardLayout from '../../../components/layout/layout';

import { styled } from "@mui/material/styles";
import { Alert, Box, Button, Grid, IconButton, InputAdornment, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Tooltip } from '@mui/material';
import { ACTIVE_COLOR, INACTIVE_COLOR, StyledTextField } from '../../../components/ui/formInput';
import { useNavigate } from 'react-router-dom';
import { useCreateCPAdminMultiStatus, useUpdateCPAdminMultiStatus, useDeleteCPAdminMultiStatus, useGetCPAdminMultiStatus } from '../../../hooks/api/user';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import ManageOrderStatusIcon from '../../../assets/img/Manage Order Status.png';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

const TableCol = styled(TableCell)(({ theme }) => ({
  border: '1px solid rgba(224, 224, 224, 1)',
}));


export interface MultiStatus {
  recordId: number;
  email: string;
  acct1: string;
  acct2: string;
  acct3: string;
  acct4: string;
  acct5: string;
  acct6: string;
  acct7: string;
  acct8: string;
}


type SortField = 'email' | 'accounts';
type SortDirection = 'asc' | 'desc' | null;
type SortState = { field: SortField | null; direction: SortDirection };

type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  email: ValidateField;
  account: ValidateField;
};

type accountTemp = {
  acct1: string;
  acct2: string;
  acct3: string;
  acct4: string;
  acct5: string;
  acct6: string;
  acct7: string;
  acct8: string;
}

const AdminManageOrder: FunctionComponent = () => {
  const navigate = useNavigate();
  const accountLimit = 6;
  const { data: multiStatus, isFetched: hasFetched, refetch: refetchMultiStatus } = useGetCPAdminMultiStatus();
  const { mutateAsync: deleteMultiStatus } = useDeleteCPAdminMultiStatus();
  const { mutateAsync: createMultiStatus } = useCreateCPAdminMultiStatus();
  const { mutateAsync: updateMultiStatus } = useUpdateCPAdminMultiStatus();
  
  const [email, setEmail] = useState('');
  const [account, setAccount] = useState('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>();
  const [ disabled, setDisabled ] = useState<boolean>(false);
  const [createError, setCreateError] = useState<string>('');
  const [createSuccess, setCreateSuccess] = useState<boolean>();
  const [ createDisabled, setCreateDisabled ] = useState<boolean>(false);
  const [totalAccounts, setTotalAccounts] = useState<number>(0);
  const [editingKey, setEditingKey] = useState<keyof typeof tempAccount | null>(null);
  const [tempAccount, setTempAccount] = useState<accountTemp>({
    acct1: '',
    acct2: '',
    acct3: '',
    acct4: '',
    acct5: '',
    acct6: '',
    acct7: '',
    acct8: ''
  });
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MultiStatus | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editAccounts, setEditAccounts] = useState<accountTemp>({
    acct1: '',
    acct2: '',
    acct3: '',
    acct4: '',
    acct5: '',
    acct6: '',
    acct7: '',
    acct8: ''
  });
  const [editAccount, setEditAccount] = useState('');
  const [editTotalAccounts, setEditTotalAccounts] = useState(0);
  const [editEditingKey, setEditEditingKey] = useState<keyof typeof editAccounts | null>(null);
  const [editError, setEditError] = useState<string>('');
  const [editSuccess, setEditSuccess] = useState<boolean>();
  const [editDisabled, setEditDisabled] = useState<boolean>(false);
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState<MultiStatus | null>(null);

  const formValidation: ValidateFieldsState = {
    email: { invalid: false, error: '' },
    account: { invalid: false, error: '' }
  };
  const [validateFields, setValidateFields] = useState<ValidateFieldsState>(formValidation);

  // Client-side sorting state (removed pagination)
  const [sortField, setSortField] = useState<SortField>('email');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Get all multi-status data without pagination
  const allMultiStatusData = useMemo(() => {
    return multiStatus || [];
  }, [multiStatus]);

  // Sort all data client-side (removed pagination)
  const sortedData = useMemo(() => {
    if (!allMultiStatusData.length) return [];
    
    return [...allMultiStatusData].sort((a, b) => {
      let aValue: any, bValue: any;
      if (sortField === 'email') {
        aValue = String(a.email || '').toLowerCase();
        bValue = String(b.email || '').toLowerCase();
      } else if (sortField === 'accounts') {
        aValue = [a.acct1, a.acct2, a.acct3, a.acct4, a.acct5, a.acct6, a.acct7, a.acct8].filter(Boolean).join(' ').toLowerCase();
        bValue = [b.acct1, b.acct2, b.acct3, b.acct4, b.acct5, b.acct6, b.acct7, b.acct8].filter(Boolean).join(' ').toLowerCase();
      }
      if (sortDirection === 'asc') return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });
  }, [allMultiStatusData, sortField, sortDirection]);

  // Handle sorting (removed pagination reset)
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort icon
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <UnfoldMoreIcon fontSize="small" />;
    if (sortDirection === 'asc') return <ArrowUpwardIcon fontSize="small" />;
    if (sortDirection === 'desc') return <ArrowDownwardIcon fontSize="small" />;
    return <UnfoldMoreIcon fontSize="small" />;
  };



  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  };

  // Check for duplicate customer numbers within the same user
  const hasDuplicateCustomerNumbers = (accounts: accountTemp, excludeRecordId?: number) => {
    const accountValues = Object.values(accounts).filter(Boolean);
    const duplicates: string[] = [];
    
    // Only check for duplicates within the same user's account list
    const seen = new Set<string>();
    for (const account of accountValues) {
      if (seen.has(account)) {
        duplicates.push(account);
      } else {
        seen.add(account);
      }
    }
    
    return duplicates;
  };

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    if (!email?.trim()) {
      errors.email = { invalid: true, error: 'Email is required' };
      valid = false;
    } else if (!isValidEmail(email)) {
      errors.email = { invalid: true, error: 'Please enter a valid email address' };
      valid = false;
    } else {
      errors.email = { invalid: false, error: '' };
    } 

    const accountValues = Object.values(tempAccount || {});
    const allEmptyAccounts = accountValues.every(val => !val?.trim());
    if (allEmptyAccounts) {
      errors.account = {
        invalid: true,
        error: 'Customer account is required',
      };
      valid = false;
    } else {
      // Check for duplicate customer numbers within the same user
      const duplicates = hasDuplicateCustomerNumbers(tempAccount);
      if (duplicates.length > 0) {
        errors.account = {
          invalid: true,
          error: `Customer number "${duplicates[0]}" is duplicated in your account list`,
        };
        valid = false;
      } else {
        errors.account = { invalid: false, error: '' };
      }
    }

    setValidateFields(errors);
    return valid;
  };

  const handleAction = async (e: React.FormEvent) => {
    setCreateError('');
    setValidateFields(formValidation);
    if (!validateForm()) {
      return;
    }
    try {
      setCreateDisabled(true);
      const result = await createMultiStatus({email: email, ...tempAccount});
      if (result?.status === 200) {
        setCreateSuccess(true);
        // Refetch data to show new record
        refetchMultiStatus();
        setTimeout(() => {
          setCreateSuccess(false);
          setEmail('');
          setAccount('');
          setTempAccount({
            acct1: '',
            acct2: '',
            acct3: '',
            acct4: '',
            acct5: '',
            acct6: '',
            acct7: '',
            acct8: ''
          });
          setCreateDisabled(false);
        }, 3000);
      } else {
        setCreateDisabled(false);
        // Handle validation errors from API response
        if (result?.data?.error) {
          setCreateError(result.data.error);
        } else if (result?.data?.message) {
          setCreateError(result.data.message);
        } else {
          setCreateError('An error occurred during creation. Please try again.');
        }
        setTimeout(() => {
          setCreateError('');
        }, 3000);
      }
    } catch (err: any) {
      setCreateDisabled(false);
      if (err?.response?.data?.error) {
        setCreateError(err.response.data.error);
      } else if (err?.response?.data?.message) {
        setCreateError(err.response.data.message);
      } else if (err?.message) {
        setCreateError(err.message);
      } else {
        setCreateError('An error occurred during login. Please try again.');
      }
    }
  }

  const handleTempAddForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    if (!account?.trim()) {
      errors.account = { invalid: true, error: 'Customer account is required' };
      valid = false;
    } else if (!/^\d+$/.test(account.trim())) {
      errors.account = { invalid: true, error: 'Customer account must be numbers only' };
      valid = false;
    } else {
      errors.account = { invalid: false, error: '' };
    }

    setValidateFields(errors);
    return valid;
  }
  const handleDeleteAccount = (key: keyof typeof tempAccount) => {
    const updatedAccounts = { ...tempAccount };

    const accountKeys: Array<keyof typeof tempAccount> = [
      'acct1',
      'acct2',
      'acct3',
      'acct4',
      'acct5',
      'acct6',
      'acct7',
      'acct8',
    ];

    const startIndex = accountKeys.findIndex(k => k === key);
    if (startIndex === -1) return; // sanity check

    for (let i = startIndex; i < accountKeys.length - 1; i++) {
      const thisKey = accountKeys[i];
      const nextKey = accountKeys[i + 1];
      updatedAccounts[thisKey] = updatedAccounts[nextKey];
    }

    const lastKey = accountKeys[accountKeys.length - 1];
    updatedAccounts[lastKey] = '';

    setTempAccount(updatedAccounts);

    setTotalAccounts(prev => Math.max(prev - 1, 0));
  };

  const handleEditAccount = (key: string) => {
    const value = tempAccount[key as keyof typeof tempAccount];
    setAccount(value);
    setEditingKey(key as keyof typeof tempAccount);
  };

  const handleTempAddAction = async (e: React.FormEvent) => {
    setCreateError('');
    setValidateFields(formValidation);

    if (!handleTempAddForm()) return;

    if (!editingKey) {
      if (totalAccounts >= accountLimit) {
        setCreateError(`You can only add up to ${accountLimit} accounts.`);
        return;
      }
      setTotalAccounts(prev => prev + 1);
    }

    const updatedAccount = { ...tempAccount };
    const newValue = account.trim();

    if (editingKey) {
      updatedAccount[editingKey] = newValue;
      setEditingKey(null);
    } else {
      const accountKeys = Object.keys(updatedAccount) as Array<keyof typeof updatedAccount>;
      const firstEmptyKey = accountKeys.find(key => !updatedAccount[key]?.trim());

      if (firstEmptyKey) {
        updatedAccount[firstEmptyKey] = newValue;
      } else {
        setCreateError('All account slots are already filled.');
        return;
      }
    }
    
    // Check for duplicates after adding the new account
    const duplicates = hasDuplicateCustomerNumbers(updatedAccount);
    if (duplicates.length > 0) {
      setCreateError(`Customer number "${duplicates[0]}" is duplicated in your account list`);
      return;
    }
    
    setTempAccount(updatedAccount);
    setAccount('');
  };

  // Handle edit button click
  const handleEditClick = (record: MultiStatus) => {
    setEditingRecord(record);
    setEditEmail(record.email);
    const accounts = {
      acct1: record.acct1 || '',
      acct2: record.acct2 || '',
      acct3: record.acct3 || '',
      acct4: record.acct4 || '',
      acct5: record.acct5 || '',
      acct6: record.acct6 || '',
      acct7: record.acct7 || '',
      acct8: record.acct8 || ''
    };
    setEditAccounts(accounts);
    setEditTotalAccounts(Object.values(accounts).filter(Boolean).length);
    setEditAccount('');
    setEditEditingKey(null);
    setEditError('');
    setEditSuccess(false);
    setEditDialogOpen(true);
  };

  // Handle delete button click
  const handleDeleteClick = (record: MultiStatus) => {
    setDeletingRecord(record);
    setDeleteDialogOpen(true);
  };

  // Handle individual delete confirmation
  const handleConfirmDelete = async () => {
    if (!deletingRecord) return;
    
    try {
      setDisabled(true);
      const result = await deleteMultiStatus([deletingRecord.email]);
      if (result?.status === 200) {
        setSuccess(true);
        // Refetch data to show updated information
        refetchMultiStatus();
        setTimeout(() => {
          setSuccess(false);
          setDisabled(false);
        }, 3000);
      } else {
        setDisabled(false);
        setError('An error occurred during deletion. Please try again.');
        setTimeout(() => {
          setError('');
        }, 3000);
      }
    } catch (err: any) {
      setDisabled(false);
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('An error occurred during deletion. Please try again.');
      }
    } finally {
      setDeleteDialogOpen(false);
      setDeletingRecord(null);
    }
  };

  // Edit form validation
  const validateEditForm = () => {
    const accountValues = Object.values(editAccounts || {});
    const allEmptyAccounts = accountValues.every(val => !val?.trim());
    if (allEmptyAccounts) {
      setEditError('At least one customer account is required');
      return false;
    }
    
    // Check for duplicate customer numbers within the same user
    const duplicates = hasDuplicateCustomerNumbers(editAccounts, editingRecord?.recordId);
    if (duplicates.length > 0) {
      setEditError(`Customer number "${duplicates[0]}" is duplicated in your account list`);
      return false;
    }
    
    return true;
  };

  // Handle edit account add/update
  const handleEditAccountAdd = () => {
    setEditError('');
    
    if (!editAccount?.trim()) {
      setEditError('Customer account is required');
      return;
    }
    
    if (!/^\d+$/.test(editAccount.trim())) {
      setEditError('Customer account must be numbers only');
      return;
    }

    if (!editEditingKey) {
      if (editTotalAccounts >= accountLimit) {
        setEditError(`You can only add up to ${accountLimit} accounts.`);
        return;
      }
      setEditTotalAccounts(prev => prev + 1);
    }

    const updatedAccount = { ...editAccounts };
    const newValue = editAccount.trim();

    if (editEditingKey) {
      updatedAccount[editEditingKey] = newValue;
      setEditEditingKey(null);
    } else {
      const accountKeys = Object.keys(updatedAccount) as Array<keyof typeof updatedAccount>;
      const firstEmptyKey = accountKeys.find(key => !updatedAccount[key]?.trim());

      if (firstEmptyKey) {
        updatedAccount[firstEmptyKey] = newValue;
      } else {
        setEditError('All account slots are already filled.');
        return;
      }
    }
    
    // Check for duplicates after adding the new account
    const duplicates = hasDuplicateCustomerNumbers(updatedAccount, editingRecord?.recordId);
    if (duplicates.length > 0) {
      setEditError(`Customer number "${duplicates[0]}" is duplicated in your account list`);
      return;
    }
    
    setEditAccounts(updatedAccount);
    setEditAccount('');
  };

  // Handle edit account delete
  const handleEditAccountDelete = (key: keyof typeof editAccounts) => {
    const updatedAccounts = { ...editAccounts };

    const accountKeys: Array<keyof typeof editAccounts> = [
      'acct1',
      'acct2',
      'acct3',
      'acct4',
      'acct5',
      'acct6',
      'acct7',
      'acct8',
    ];

    const startIndex = accountKeys.findIndex(k => k === key);
    if (startIndex === -1) return;

    for (let i = startIndex; i < accountKeys.length - 1; i++) {
      const thisKey = accountKeys[i];
      const nextKey = accountKeys[i + 1];
      updatedAccounts[thisKey] = updatedAccounts[nextKey];
    }

    const lastKey = accountKeys[accountKeys.length - 1];
    updatedAccounts[lastKey] = '';

    setEditAccounts(updatedAccounts);
    setEditTotalAccounts(prev => Math.max(prev - 1, 0));
  };

  // Handle edit account edit
  const handleEditAccountEdit = (key: string) => {
    const value = editAccounts[key as keyof typeof editAccounts];
    setEditAccount(value);
    setEditEditingKey(key as keyof typeof editAccounts);
  };

  // Handle edit save
  const handleEditSave = async () => {
    setEditError('');
    
    if (!validateEditForm()) {
      return;
    }

    if (!editingRecord) return;

    try {
      setEditDisabled(true);
      const result = await updateMultiStatus({
        recordId: editingRecord.recordId,
        email: editingRecord.email,
        ...editAccounts
      });
      
      if (result?.status === 200) {
        setEditSuccess(true);
        // Refetch data to show updated information
        refetchMultiStatus();
        setTimeout(() => {
          setEditSuccess(false);
          setEditDialogOpen(false);
          setEditDisabled(false);
        }, 2000);
      } else {
        setEditDisabled(false);
        // Handle validation errors from API response
        if (result?.data?.error) {
          setEditError(result.data.error);
        } else if (result?.data?.message) {
          setEditError(result.data.message);
        } else {
          setEditError('An error occurred during update. Please try again.');
        }
        setTimeout(() => {
          setEditError('');
        }, 3000);
      }
    } catch (err: any) {
      setEditDisabled(false);
      if (err?.response?.data?.error) {
        setEditError(err.response.data.error);
      } else if (err?.response?.data?.message) {
        setEditError(err.response.data.message);
      } else if (err?.message) {
        setEditError(err.message);
      } else {
        setEditError('An error occurred during update. Please try again.');
      }
    }
  };

  // Handle edit dialog close
  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditingRecord(null);
    setEditEmail('');
    setEditAccounts({
      acct1: '',
      acct2: '',
      acct3: '',
      acct4: '',
      acct5: '',
      acct6: '',
      acct7: '',
      acct8: ''
    });
    setEditAccount('');
    setEditTotalAccounts(0);
    setEditEditingKey(null);
    setEditError('');
    setEditSuccess(false);
  };

  // Handle delete dialog close
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setDeletingRecord(null);
  };

  return (
    <>
      <DashboardLayout titleIcon={<img src={ManageOrderStatusIcon} alt="Manage Order Status" style={{ width: 25, height: 25 }} />} title={"Manage Order Status"} initialBreadcrumbs={"Channel Partner"} levelBreadcrumbs={'CP Admin'} breadcrumbs={'Multi Status'} activeMenu={'admin'}>
        <Typography variant="body1" mb={4}>
          Order Status for Multiple Customer Accounts
        </Typography>
        
        <Grid container spacing={2} mb={2}>
          <Grid size={6}>
            <Typography sx={{ mb: 0.5 }}>
              User Email Address
            </Typography>
            <StyledTextField
              label="User Email Address"
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
              error={validateFields.email.invalid}
              helperText={validateFields.email.error}
            />
          </Grid>
          <Grid size={6} sx={{ marginTop: -3 }}>
            <Typography sx={{ mb: 0.5 }}>
              Customer Account Number
            </Typography>
            <Typography variant="caption" color="error" sx={{ mb: 0.5 }}>
              (Do not include user's own customer account)
            </Typography>
            <TextField
              fullWidth
              label="Customer Account Number"
              variant="outlined"
              margin="normal"
              type="text"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              error={validateFields.account.invalid}
              helperText={validateFields.account.error}
              FormHelperTextProps={{ sx: { marginLeft: '0px' } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={handleTempAddAction}
                      disabled={!editingKey && totalAccounts >= accountLimit}
                    >
                      {editingKey ? <CheckCircleOutlineIcon /> : <AddCircleOutlineIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                sx: {
                  color: validateFields.account.invalid ? '#d32f2f' : INACTIVE_COLOR,
                  '&.Mui-focused': {
                    color: ACTIVE_COLOR,
                  },
                  '&.MuiInputLabel-shrink': {
                    color: ACTIVE_COLOR,
                  },
                  '&:hover': {
                    color: ACTIVE_COLOR,
                  },
                },
              }}
              sx={{
                marginTop: '0px',
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: ACTIVE_COLOR,
                  },
                  '&:hover fieldset': {
                    borderColor: ACTIVE_COLOR,
                  },
                },
              }}
            />
            <Grid size={12}>
              {Object.entries(tempAccount).map(([key, value], index) =>
                value ? (
                  <Grid>
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2">{value}</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" onClick={() => handleEditAccount(key)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteAccount(key as keyof accountTemp)} sx={{ color: '#c3183c' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>
                ) : null
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid size={12}>
          {createError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {createError}
            </Alert>
          )}
          {
            createSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Account created successfully!
              </Alert>
            )
          }
        </Grid>
        <Box sx={{ mt: 3, display: "flex", alignItems: "center", justifyContent: 'start', gap: 3 }}>
          <Button disabled={createDisabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '100px' }} onClick={handleAction}>
            ADD USER
          </Button>
          <Button disabled={createDisabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '100px' }} onClick={() => {
            setEmail('');
            setAccount('');
          }}>
            CLEAR FIELDS
          </Button>
        </Box>

        <Box sx={{ width: '100%', maxWidth: '100%', mt: 5 }}>
          <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "100%", overflowX: "auto", borderRadius: 0 }}>
            <Table size="small" aria-label="user list table">
              <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
                <TableRow>
                  <TableCol
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                    onClick={() => handleSort('email')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      User List
                      <Tooltip title="Click to sort">{getSortIcon('email')}</Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                    onClick={() => handleSort('accounts')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      Assigned Accounts
                      <Tooltip title="Click to sort">{getSortIcon('accounts')}</Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol>Actions</TableCol>
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  hasFetched ? (
                    sortedData.length === 0 ?
                      <TableRow>
                        <TableCol colSpan={3}>
                          No Data Found
                        </TableCol>
                      </TableRow>
                    : sortedData.map((row: MultiStatus, idx: number) => (
                      <TableRow key={row.recordId}>
                        <TableCol>{row.email}</TableCol>
                        <TableCol>
                          {[
                            row.acct1,
                            row.acct2,
                            row.acct3,
                            row.acct4,
                            row.acct5,
                            row.acct6,
                            row.acct7,
                            row.acct8
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        </TableCol>
                        <TableCol>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Tooltip title="Edit user permissions">
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label="edit user"
                                onClick={() => handleEditClick(row)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete user permissions">
                              <IconButton
                                size="small"
                                color="error"
                                aria-label="delete user"
                                onClick={() => handleDeleteClick(row)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCol>
                      </TableRow>
                    ))
                  ) : (
                    <>
                      <TableRow>
                        <TableCol colSpan={3}>
                          <Skeleton width="38%" />
                          <Skeleton width="100%" />
                          <Skeleton width="100%" />
                          <Skeleton width="61%" />
                        </TableCol>
                      </TableRow>
                    </>
                  )
                }
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Grid size={12}>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {
            success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Account removed successfully!
              </Alert>
            )
          }
        </Grid>
        <Box sx={{ mt: 3, display: "flex", alignItems: "center", justifyContent: 'start', gap: 3 }}>
          <Button variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important' }} onClick={() => navigate(`/admin`)}>
            BACK
          </Button>
        </Box>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onClose={handleEditDialogClose} maxWidth="md" fullWidth>
          <DialogTitle>Edit User Permissions</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Email: {editingRecord?.email}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                Customer Account Numbers
              </Typography>
              <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                (Do not include user's own customer account)
              </Typography>
              
              <TextField
                fullWidth
                label="Customer Account Number"
                variant="outlined"
                margin="normal"
                type="text"
                value={editAccount}
                onChange={(e) => setEditAccount(e.target.value)}
                FormHelperTextProps={{ sx: { marginLeft: '0px' } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={handleEditAccountAdd}
                        disabled={!editEditingKey && editTotalAccounts >= accountLimit}
                      >
                        {editEditingKey ? <CheckCircleOutlineIcon /> : <AddCircleOutlineIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  sx: {
                    color: INACTIVE_COLOR,
                    '&.Mui-focused': {
                      color: ACTIVE_COLOR,
                    },
                    '&.MuiInputLabel-shrink': {
                      color: ACTIVE_COLOR,
                    },
                    '&:hover': {
                      color: ACTIVE_COLOR,
                    },
                  },
                }}
                sx={{
                  marginTop: '0px',
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: ACTIVE_COLOR,
                    },
                    '&:hover fieldset': {
                      borderColor: ACTIVE_COLOR,
                    },
                  },
                }}
              />
              
              <Box sx={{ mt: 2 }}>
                {Object.entries(editAccounts).map(([key, value], index) =>
                  value ? (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                      <Typography variant="body2">{value}</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" onClick={() => handleEditAccountEdit(key)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleEditAccountDelete(key as keyof accountTemp)} sx={{ color: '#c3183c' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : null
                )}
              </Box>

              {editError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {editError}
                </Alert>
              )}
              {editSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  User permissions updated successfully!
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditDialogClose} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={handleEditSave} 
              color="primary" 
              variant="contained"
              disabled={editDisabled}
              sx={{ backgroundColor: '#c3183c' }}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the permissions for user "{deletingRecord?.email}"?
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteDialogClose} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDelete} 
              color="error" 
              variant="contained"
              disabled={disabled}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </DashboardLayout>
    </>
  )
};

export default AdminManageOrder;